import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/layouts/public-layout';
import { AuthedLayout } from '@/layouts/authed-layout';
import { RequireAuth, RequireAdmin } from './guards';

// Eager — landing & auth pages are the first things users see
import { HomePage } from '@/pages/public/home-current';
import { PricingPage } from '@/pages/public/pricing';
import { LoginPage } from '@/pages/auth/login';
import { JoinPage } from '@/pages/auth/join';

// Lazy — everything else is code-split
const ContactPage = lazy(() => import('@/pages/public/contact').then((m) => ({ default: m.ContactPage })));
const StartJourneyPage = lazy(() =>
  import('@/pages/public/onboarding').then((m) => ({ default: m.StartJourneyPage })),
);
const OnboardingResultsPage = lazy(() =>
  import('@/pages/public/onboarding-results').then((m) => ({ default: m.OnboardingResultsPage })),
);
const DevotionalPage = lazy(() =>
  import('@/pages/public/devotional').then((m) => ({ default: m.DevotionalPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/stubs').then((m) => ({ default: m.NotFoundPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/forgot-password').then((m) => ({ default: m.ForgotPasswordPage })),
);
const AuthCallbackPage = lazy(() =>
  import('@/pages/auth/callback').then((m) => ({ default: m.AuthCallbackPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/reset-password').then((m) => ({ default: m.ResetPasswordPage })),
);
const UserProfilePage = lazy(() =>
  import('@/pages/stubs').then((m) => ({ default: m.UserProfilePage })),
);
const AccountPage = lazy(() =>
  import('@/pages/dashboard/account').then((m) => ({ default: m.AccountPage })),
);
const BasketPage = lazy(() => import('@/pages/stubs').then((m) => ({ default: m.BasketPage })));

const DashboardPage = lazy(() =>
  import('@/pages/dashboard').then((m) => ({ default: m.DashboardPage })),
);

const CatalogPage = lazy(() =>
  import('@/pages/course').then((m) => ({ default: m.CatalogPage })),
);
const SubscriberCatalogPage = lazy(() =>
  import('@/pages/course/browse').then((m) => ({ default: m.SubscriberCatalogPage })),
);
const CourseInfoPage = lazy(() =>
  import('@/pages/course').then((m) => ({ default: m.CourseInfoPage })),
);
const MyCoursesPage = lazy(() =>
  import('@/pages/course').then((m) => ({ default: m.MyCoursesPage })),
);
const CourseViewerPage = lazy(() =>
  import('@/pages/course').then((m) => ({ default: m.CourseViewerPage })),
);
const BonusVideoPage = lazy(() =>
  import('@/pages/course').then((m) => ({ default: m.BonusVideoPage })),
);

const SisterhoodFeedPage = lazy(() =>
  import('@/pages/community').then((m) => ({ default: m.SisterhoodFeedPage })),
);
const PostDetailPage = lazy(() =>
  import('@/pages/community').then((m) => ({ default: m.PostDetailPage })),
);
const TagFeedPage = lazy(() =>
  import('@/pages/community').then((m) => ({ default: m.TagFeedPage })),
);
const ChatPage = lazy(() => import('@/pages/community/chat').then((m) => ({ default: m.ChatPage })));

const AdminLayout = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminLayout })),
);
const AdminDashboardPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminDashboardPage })),
);
const AdminCoursesPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminCoursesPage })),
);
const AdminNewCoursePage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminCourseEditorPage })),
);
const AdminCourseEditorPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminCourseEditorPage })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminPostsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminPostsPage })),
);
const AdminPlansPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminPlansPage })),
);
const AdminReportsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminReportsPage })),
);
const AdminDevotionalSubscribersPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminDevotionalSubscribersPage })),
);

/* ---------- Suspense fallback ---------- */
function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
    </div>
  );
}

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteFallback />}>{children}</Suspense>
);

/* ---------- Router config ---------- */
export const router = createBrowserRouter([
  // ============= PUBLIC (with nav + footer) =============
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/contact', element: <Lazy><ContactPage /></Lazy> },
      { path: '/devotional', element: <Lazy><DevotionalPage /></Lazy> },
      { path: '/catalog', element: <Lazy><CatalogPage /></Lazy> },
      { path: '/courses', element: <Navigate to="/catalog" replace /> },
      { path: '/course-info/:id', element: <Lazy><CourseInfoPage /></Lazy> },
    ],
  },

  // Onboarding — full-screen, no nav chrome
  { path: '/start-journey', element: <Lazy><StartJourneyPage /></Lazy> },
  { path: '/start-journey/results', element: <Lazy><OnboardingResultsPage /></Lazy> },

  // ============= AUTH (standalone, no PublicLayout chrome) =============
  { path: '/login', element: <LoginPage /> },
  { path: '/join', element: <JoinPage /> },
  { path: '/forgot-password', element: <Lazy><ForgotPasswordPage /></Lazy> },
  { path: '/auth/callback', element: <Lazy><AuthCallbackPage /></Lazy> },
  { path: '/auth/reset-password', element: <Lazy><ResetPasswordPage /></Lazy> },

  // ============= AUTHENTICATED =============
  {
    element: <RequireAuth />,
    children: [
      // Chat — full screen
      { path: '/chat', element: <Lazy><ChatPage /></Lazy> },
      {
        element: <AuthedLayout />,
        children: [
          { path: '/dashboard', element: <Lazy><DashboardPage /></Lazy> },
          { path: '/my-courses', element: <Lazy><MyCoursesPage /></Lazy> },
          { path: '/browse', element: <Lazy><SubscriberCatalogPage /></Lazy> },
          { path: '/course/:id', element: <Lazy><CourseViewerPage /></Lazy> },
          { path: '/bonus-video/:id', element: <Lazy><BonusVideoPage /></Lazy> },
          { path: '/sisterhood', element: <Lazy><SisterhoodFeedPage /></Lazy> },
          { path: '/community', element: <Navigate to="/sisterhood" replace /> },
          { path: '/sisterhood/post/:id', element: <Lazy><PostDetailPage /></Lazy> },
          { path: '/sisterhood/tag/:tag', element: <Lazy><TagFeedPage /></Lazy> },

          { path: '/user-profile', element: <Lazy><UserProfilePage /></Lazy> },
          { path: '/my-account', element: <Lazy><AccountPage /></Lazy> },
          { path: '/basket', element: <Lazy><BasketPage /></Lazy> },
        ],
      },
    ],
  },

  // ============= ADMIN =============
  {
    element: <RequireAdmin />,
    children: [
      {
        element: <Lazy><AdminLayout /></Lazy>,
        children: [
          { path: '/admin', element: <Navigate to="/admin/dashboard" replace /> },
          { path: '/admin/dashboard', element: <Lazy><AdminDashboardPage /></Lazy> },
          { path: '/admin/courses', element: <Lazy><AdminCoursesPage /></Lazy> },
          { path: '/admin/courses/new', element: <Lazy><AdminNewCoursePage /></Lazy> },
          { path: '/admin/courses/:id', element: <Lazy><AdminCourseEditorPage /></Lazy> },
          { path: '/admin/users', element: <Lazy><AdminUsersPage /></Lazy> },
          { path: '/admin/posts', element: <Lazy><AdminPostsPage /></Lazy> },
          { path: '/admin/plans', element: <Lazy><AdminPlansPage /></Lazy> },
          { path: '/admin/reports', element: <Lazy><AdminReportsPage /></Lazy> },
          { path: '/admin/devotional-subscribers', element: <Lazy><AdminDevotionalSubscribersPage /></Lazy> },
        ],
      },
    ],
  },

  // ============= 404 =============
  { path: '*', element: <Lazy><NotFoundPage /></Lazy> },
]);
